/**
 * Enum for contract-level inspection statuses.
 * These statuses reflect the combined inspection state (Pick-up, Drop-off, Employee).
 *
 * @enum {string}
 * @property {string} SCHEDULED - Contract inspection is scheduled but not yet started
 * @property {string} PENDING_PICK_UP - Waiting for client to perform initial Pick-up inspection
 * @property {string} MISSED_PICK_UP - Client missed the scheduled Pick-up inspection
 * @property {string} PENDING_DROP_OFF - Waiting for Drop-off inspection before vehicle return
 * @property {string} PENDING_EMPLOYEE - Awaiting internal Employee inspection after return
 * @property {string} ESTIMATE_REQUESTED - Rental user requested estimate; visible to inspectors
 * @property {string} COMPLETED - All inspections completed and contract inspection is finalized
 * @property {string} CANCELED - Contract inspection was canceled
 * @property {string} DELETED - Contract inspection was deleted
 */
export enum StatusContractInspection {
  SCHEDULED = 'SCHEDULED',
  PENDING_PICK_UP = 'PENDING_PICK_UP',
  MISSED_PICK_UP = 'MISSED_PICK_UP',
  PENDING_DROP_OFF = 'PENDING_DROP_OFF',
  PENDING_EMPLOYEE = 'PENDING_EMPLOYEE',
  ESTIMATE_REQUESTED = 'ESTIMATE_REQUESTED',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  DELETED = 'DELETED',
}

/**
 * Enum for contract sorting fields.
 * Used to specify which field to sort contracts by in queries or UI.
 *
 * @enum {string}
 * @property {string} id - Unique identifier of the contract
 * @property {string} contractNumber - Contract number
 * @property {string} vinNumber - Vehicle Identification Number
 * @property {string} status - Status of the contract
 * @property {string} pickUpDate - Date of vehicle pick-up
 * @property {string} dropOffDate - Date of vehicle drop-off
 * @property {string} createdAt - Date the contract was created
 */
export enum ContractSortField {
  id = 'id',
  contractNumber = 'contractNumber',
  vinNumber = 'vinNumber',
  status = 'status',
  pickUpDate = 'pickUpDate',
  dropOffDate = 'dropOffDate',
  createdAt = 'createdAt',
  employeeDate = 'employeeDate',
}

/**
 * Enum for fuel levels in a vehicle.
 * Used to indicate the current fuel status.
 *
 * @enum {string}
 * @property {string} NOT_SELECTED - No fuel level selected
 * @property {string} EMPTY - Fuel tank is empty
 * @property {string} QUARTER - Fuel tank is at a quarter
 * @property {string} HALF - Fuel tank is half full
 * @property {string} THREE_QUARTER - Fuel tank is three quarters full
 * @property {string} FULL - Fuel tank is full
 */
export enum FuelLevel {
  NOT_SELECTED = 'NOT_SELECTED',
  EMPTY = 'EMPTY',
  QUARTER = 'QUARTER',
  HALF = 'HALF',
  THREE_QUARTER = 'THREE_QUARTER',
  FULL = 'FULL',
}

export const FUEL_LEVEL_VALUES: Record<FuelLevel, number | null> = {
  [FuelLevel.NOT_SELECTED]: null,
  [FuelLevel.EMPTY]: 0,
  [FuelLevel.QUARTER]: 25,
  [FuelLevel.HALF]: 50,
  [FuelLevel.THREE_QUARTER]: 75,
  [FuelLevel.FULL]: 100,
};

/**
 * Enum for claim actors.
 * Indicates who is responsible for or involved in a claim.
 *
 * @enum {string}
 * @property {string} CUSTOMER - The customer involved in the claim
 * @property {string} EMPLOYEE - The employee involved in the claim
 */
export enum ClaimActor {
  CUSTOMER = 'CUSTOMER',
  EMPLOYEE = 'EMPLOYEE',
}

/**
 * Enum for claim statuses.
 * Represents the different states a claim can be in during its lifecycle.
 *
 * @enum {string}
 * @property {string} STARTED - Claim has been initiated by the customer or employee but is not yet under review.
 * @property {string} IN_REVIEW - Claim is currently being evaluated by the claims department or relevant personnel.
 * @property {string} INFO_REQUESTED - Additional information has been requested from the claimant to proceed with the evaluation.
 * @property {string} PAYMENT_REQUESTED - Payment for the claim has been requested and is pending processing.
 * @property {string} CONTESTED - The claim is being disputed by one of the parties involved.
 * @property {string} PAID - The claim has been processed and payment has been made to the claimant.
 * @property {string} WRITTEN_OFF - The claim has been written off, indicating that it will not be pursued further.
 * @property {string} CANCELED - The claim has been canceled by the claimant or the company.
 * @property {string} DELETED - The claim record has been deleted from the system.
 */
export enum ClaimStatus {
  STARTED = 'STARTED',
  IN_REVIEW = 'IN_REVIEW',
  INFO_REQUESTED = 'INFO_REQUESTED',
  PAYMENT_REQUESTED = 'PAYMENT_REQUESTED',
  CONTESTED = 'CONTESTED',
  PAID = 'PAID',
  WRITTEN_OFF = 'WRITTEN_OFF',
  CANCELED = 'CANCELED',
  DELETED = 'DELETED',
}

/**
 * Enum for actions that can be performed on a contract inspection activity.
 * Used to specify the type of activity action in logs or workflows.
 *
 * @enum {string}
 * @property {string} create - Indicates creation of a contract inspection activity
 * @property {string} delete - Indicates deletion of a contract inspection activity
 * @property {string} addEmployeeInspection - Indicates adding an employee inspection to a contract
 * @property {string} updateFlow - Indicates updating the flow of a contract inspection
 * @property {string} update - Indicates updating the contract inspection details
 * @property {string} addExtraFees - Indicates adding extra fees to a contract inspection
 * @property {string} updateInfo - Indicates updating information related to a contract inspection
 * @property {string} setClaimReport - Indicates setting a claim report for a contract inspection
 * @property {string} updateStatus - Indicates updating the status of a contract inspection
 * @property {string} skipStatus - Indicates skipping the status update of a contract inspection
 * @property {string} skipStatusThroughStart - Indicates skipping the status through the start of a contract inspection
 * @property {string} updateClaimStatus - Indicates updating the claim status associated with a contract inspection
 * @property {string} requestEstimate - Indicates requesting an estimate for a contract inspection
 */
export enum ActivityActionContractInspection {
  create = 'create',
  delete = 'delete',
  addEmployeeInspection = 'addEmployeeInspection',
  updateFlow = 'updateFlow',
  update = 'update',
  addExtraFees = 'addExtraFees',
  updateInfo = 'updateInfo',
  setClaimReport = 'setClaimReport',
  updateStatus = 'updateStatus',
  skipStatus = 'skipStatus',
  skipStatusThroughStart = 'skipStatusThroughStart',
  requestEstimate = 'requestEstimate',
  updateClaimStatus = 'updateClaimStatus',
  cancel = 'cancel',
}

/**
 * Enum for contract entity prefixes.
 * @enum {string}
 * @property {string} CONTRACT - Prefix for contract entities
 */
export enum ContractPrefix {
  CONTRACT = 'con',
}

/**
 * Enum for claim entity prefixes.
 * @enum {string}
 * @property {string} CLAIM - Prefix for claim entities
 */
export enum ClaimPrefix {
  CLAIM = 'clm',
}

/**
 * Enum for middle segments used in Contract Inspection IDs.
 * These segments identify the type of inspection in the ID structure.
 *
 * @enum {string}
 * @property {string} PICK_UP - Middle segment for Pick-up inspection IDs
 * @property {string} DROP_OFF - Middle segment for Drop-off inspection IDs
 * @property {string} EMPLOYEE - Middle segment for Employee inspection IDs
 */
export enum ContractInspectionIdMiddleSegment {
  PICK_UP = 'pck',
  DROP_OFF = 'drp',
  EMPLOYEE = 'emp',
}

/**
 * Enum for types of inspections.
 * Used to distinguish between Pick-up and Drop-off inspections.
 *
 * @enum {string}
 * @property {string} PICK_UP - Pick-up inspection
 * @property {string} DROP_OFF - Drop-off inspection
 */
export enum InspectionKind {
  PICK_UP = 'PICK_UP',
  DROP_OFF = 'DROP_OFF',
}

/**
 * Enum for the origin of damage.
 * Indicates at which stage or from which source the damage was identified.
 *
 * @enum {string}
 * @property {string} PICK_UP - Damage identified during Pick-up inspection
 * @property {string} DROP_OFF - Damage identified during Drop-off inspection
 * @property {string} EMPLOYEE - Damage identified during Employee inspection
 * @property {string} PREVIOUS_RENTAL - Damage carried over from a previous rental
 */
export enum DamageOrigin {
  PICK_UP = 'PICK_UP',
  DROP_OFF = 'DROP_OFF',
  EMPLOYEE = 'EMPLOYEE',
  PREVIOUS_RENTAL = 'PREVIOUS_RENTAL',
}
